import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Clock, CheckCircle2, XCircle, Trophy, Coins, Zap, ChevronLeft, ChevronRight, BarChart3, BookOpen, Target } from 'lucide-react';

export default function QuizPage({ params, onNavigate }) {
  const { api } = useAuth();
  const [quest, setQuest] = useState(params?.quest || null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  const questions = quest?.quiz?.questions || [];
  const totalQ = questions.length;

  useEffect(() => {
    if (quest?.quiz?.time_limit_seconds && !submitted) {
      setTimeLeft(quest.quiz.time_limit_seconds);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [quest, submitted]);

  const selectAnswer = (qIdx, optIdx) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [String(qIdx)]: optIdx }));
  };

  const handleSubmit = async () => {
    if (submitting || submitted) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const res = await api.post('/ai/submit-quiz', {
        quest_id: quest.id,
        answers
      });
      setResults(res.data);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Calculate result breakdown by difficulty/type
  const getBreakdown = () => {
    if (!results?.results) return null;
    
    const breakdown = {
      easy: { correct: 0, total: 0 },
      medium: { correct: 0, total: 0 },
      hard: { correct: 0, total: 0 }
    };
    
    results.results.forEach((r, i) => {
      const difficulty = questions[i]?.difficulty || 'medium';
      breakdown[difficulty].total++;
      if (r.is_correct) breakdown[difficulty].correct++;
    });
    
    return breakdown;
  };

  if (!quest) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-slate-600" />
          </div>
          <p className="text-slate-400 mb-4">No quiz loaded</p>
          <button 
            onClick={() => onNavigate('chapters')} 
            className="text-violet-400 hover:text-violet-300 text-sm font-medium"
          >
            Go to Chapters →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="quiz-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <button 
              data-testid="back-from-quiz" 
              onClick={() => onNavigate('chapters')} 
              className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4 text-slate-400" />
            </button>
            <div>
              <h1 className="text-sm font-bold text-white" data-testid="quiz-title">
                {quest.quiz?.title || `Quiz: ${quest.chapter_name}`}
              </h1>
              <p className="text-xs text-slate-500">{quest.subject} • {quest.class_name}</p>
            </div>
          </div>
          
          {timeLeft != null && !submitted && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
              timeLeft < 60 ? 'bg-red-500/20 border border-red-500/30' : 'bg-slate-800 border border-slate-700'
            }`} data-testid="quiz-timer">
              <Clock className={`w-4 h-4 ${timeLeft < 60 ? 'text-red-400' : 'text-slate-400'}`} />
              <span className={`text-sm font-mono font-bold ${timeLeft < 60 ? 'text-red-400' : 'text-white'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pt-5 max-w-lg mx-auto">
        {/* Progress Bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Progress</span>
            <span className="text-xs text-slate-400">
              {submitted 
                ? `${results?.correct || 0}/${totalQ} correct` 
                : `${Object.keys(answers).length}/${totalQ} answered`
              }
            </span>
          </div>
          <div className="flex gap-1">
            {questions.map((_, i) => (
              <div 
                key={i} 
                className={`h-2 flex-1 rounded-full transition-all cursor-pointer ${
                  submitted 
                    ? (results?.results?.[i]?.is_correct ? 'bg-emerald-500' : 'bg-red-500') 
                    : i === currentQ 
                      ? 'bg-violet-500' 
                      : answers[String(i)] !== undefined 
                        ? 'bg-violet-500/40' 
                        : 'bg-slate-800'
                }`} 
                onClick={() => !submitted && setCurrentQ(i)} 
                data-testid={`progress-dot-${i}`} 
              />
            ))}
          </div>
        </div>

        {!submitted ? (
          /* Question Card */
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQ}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.2 }}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5"
              data-testid={`question-card-${currentQ}`}
            >
              {/* Question Header */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs bg-violet-600/20 text-violet-400 px-2.5 py-1 rounded-full font-semibold">
                  Q{currentQ + 1}/{totalQ}
                </span>
                {questions[currentQ]?.difficulty && (
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    questions[currentQ].difficulty === 'hard' 
                      ? 'bg-red-500/10 text-red-400' 
                      : questions[currentQ].difficulty === 'medium' 
                        ? 'bg-amber-500/10 text-amber-400' 
                        : 'bg-emerald-500/10 text-emerald-400'
                  }`}>
                    {questions[currentQ].difficulty}
                  </span>
                )}
              </div>

              {/* Question Text */}
              <h2 className="text-base font-medium text-white mb-5 leading-relaxed">
                {questions[currentQ]?.question}
              </h2>

              {/* Graph/Diagram Support (if question has image) */}
              {questions[currentQ]?.image_url && (
                <div className="mb-5 rounded-xl overflow-hidden bg-slate-800 p-2">
                  <img 
                    src={questions[currentQ].image_url} 
                    alt="Question diagram"
                    className="w-full rounded-lg"
                  />
                </div>
              )}

              {/* Options */}
              <div className="space-y-3">
                {questions[currentQ]?.options?.map((opt, j) => (
                  <motion.button
                    key={j}
                    data-testid={`option-${currentQ}-${j}`}
                    onClick={() => selectAnswer(currentQ, j)}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      answers[String(currentQ)] === j
                        ? 'border-violet-500 bg-violet-600/10'
                        : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                        answers[String(currentQ)] === j 
                          ? 'bg-violet-600 text-white' 
                          : 'bg-slate-800 text-slate-500'
                      }`}>
                        {String.fromCharCode(65 + j)}
                      </div>
                      <span className={`text-sm ${
                        answers[String(currentQ)] === j ? 'text-white' : 'text-slate-300'
                      }`}>
                        {opt}
                      </span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        ) : (
          /* Results */
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="space-y-5" 
            data-testid="quiz-results"
          >
            {/* Score Card */}
            <div className={`bg-gradient-to-br rounded-2xl p-6 text-center ${
              results?.score >= 80 
                ? 'from-emerald-600/20 to-emerald-600/5 border border-emerald-500/30' 
                : results?.score >= 50 
                  ? 'from-amber-600/20 to-amber-600/5 border border-amber-500/30' 
                  : 'from-red-600/20 to-red-600/5 border border-red-500/30'
            }`}>
              {/* Score Ring */}
              <div className="relative w-28 h-28 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="50"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-slate-800"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r="50"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(results?.score || 0) * 3.14} 314`}
                    strokeLinecap="round"
                    className={`transition-all duration-1000 ${
                      results?.score >= 80 ? 'text-emerald-500' : results?.score >= 50 ? 'text-amber-500' : 'text-red-500'
                    }`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">{results?.score}%</span>
                </div>
              </div>

              <h2 className="text-xl font-bold text-white mb-1">
                {results?.score >= 80 ? 'Excellent!' : results?.score >= 50 ? 'Good Effort!' : 'Keep Practicing!'}
              </h2>
              <p className="text-sm text-slate-400">{results?.correct}/{results?.total} correct answers</p>

              {/* Rewards */}
              <div className="flex items-center justify-center gap-4 mt-5 pt-5 border-t border-slate-800/50">
                <div className="flex items-center gap-1.5 bg-violet-500/20 px-3 py-1.5 rounded-full">
                  <Zap className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-300">+{results?.xp_gained} XP</span>
                </div>
                <div className="flex items-center gap-1.5 bg-amber-500/20 px-3 py-1.5 rounded-full">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-300">+{results?.coins_gained}</span>
                </div>
                {results?.new_badges?.length > 0 && (
                  <div className="flex items-center gap-1.5 bg-pink-500/20 px-3 py-1.5 rounded-full">
                    <Trophy className="w-4 h-4 text-pink-400" />
                    <span className="text-sm font-semibold text-pink-300">{results.new_badges.length} Badge!</span>
                  </div>
                )}
              </div>
            </div>

            {/* Breakdown by Difficulty */}
            {getBreakdown() && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-violet-400" />
                  Performance Breakdown
                </h3>
                <div className="space-y-3">
                  {['easy', 'medium', 'hard'].map(diff => {
                    const data = getBreakdown()[diff];
                    if (data.total === 0) return null;
                    const percent = Math.round((data.correct / data.total) * 100);
                    return (
                      <div key={diff}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`text-xs font-medium capitalize ${
                            diff === 'easy' ? 'text-emerald-400' : diff === 'medium' ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {diff}
                          </span>
                          <span className="text-xs text-slate-400">
                            {data.correct}/{data.total} ({percent}%)
                          </span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percent}%` }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className={`h-full rounded-full ${
                              diff === 'easy' ? 'bg-emerald-500' : diff === 'medium' ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Answer Review */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Target className="w-4 h-4 text-cyan-400" />
                Answer Review
              </h3>
              {results?.results?.map((r, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-slate-900/50 border rounded-xl p-4 ${
                    r.is_correct ? 'border-emerald-500/20' : 'border-red-500/20'
                  }`} 
                  data-testid={`result-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      r.is_correct ? 'bg-emerald-500/20' : 'bg-red-500/20'
                    }`}>
                      {r.is_correct 
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> 
                        : <XCircle className="w-4 h-4 text-red-400" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white mb-2">{r.question}</p>
                      {!r.is_correct && r.correct_answer && (
                        <p className="text-xs text-emerald-400 mb-1">
                          Correct: {r.correct_answer}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">{r.explanation}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => onNavigate('chapters')}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-3.5 font-semibold text-sm transition-all"
              >
                Back to Chapters
              </button>
              <button
                data-testid="try-another-quiz"
                onClick={() => onNavigate('daily-loop')}
                className="flex-1 bg-violet-600 hover:bg-violet-500 text-white rounded-xl py-3.5 font-semibold text-sm transition-all"
              >
                Next Session
              </button>
            </div>
          </motion.div>
        )}

        {/* Navigation (when not submitted) */}
        {!submitted && (
          <div className="flex gap-3 mt-5">
            <button
              data-testid="prev-question"
              onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
              disabled={currentQ === 0}
              className="flex-1 bg-slate-800 text-slate-300 rounded-xl py-3 text-sm font-medium hover:bg-slate-700 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            {currentQ < totalQ - 1 ? (
              <button
                data-testid="next-question"
                onClick={() => setCurrentQ(currentQ + 1)}
                className="flex-1 bg-violet-600 text-white rounded-xl py-3 text-sm font-medium hover:bg-violet-500 transition-all flex items-center justify-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid="submit-quiz-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-emerald-600 text-white rounded-xl py-3 text-sm font-medium hover:bg-emerald-500 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Submit
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
